import {FolderOpen, MonitorDown, X} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/matic/components/ui/dialog';
import {Button} from '@/matic/components/ui/button';
import {Checkbox} from '@/matic/components/ui/checkbox';
import type {
  DesktopShortcutPage,
  DesktopShortcutView,
} from '@/matic/features/relay/hooks/useDesktopShortcuts';
import type {ShortcutFormat} from '@/matic/features/relay/lib/shortcuts/shortcut-file-formats';

type Props = {
  readonly open: boolean;
  readonly isSupported: boolean;
  readonly hasFolder: boolean;
  readonly folderName?: string;
  readonly pages: readonly DesktopShortcutPage[];
  readonly views: readonly DesktopShortcutView[];
  readonly selectedViewKeys: ReadonlySet<string>;
  readonly selectedViewCount: number;
  readonly onViewSelectedChange: (key: string, selected: boolean) => void;
  readonly format: ShortcutFormat;
  readonly onFormatChange: (next: ShortcutFormat) => void;
  readonly isWorking: boolean;
  readonly progress?: {written: number; total: number};
  readonly onClose: () => void;
  readonly onChooseFolder: () => void;
  readonly onUpdate: () => void;
  readonly onDownloadZip: () => void;
};

function FormatOptions({
  format,
  onChange,
}: {
  readonly format: ShortcutFormat;
  readonly onChange: (next: ShortcutFormat) => void;
}) {
  return (
    <details className="text-xs text-neutral-500">
      <summary className="cursor-pointer select-none hover:text-neutral-700">
        Format: {format === 'url' ? 'Windows' : 'macOS'}
      </summary>
      <div className="mt-2 flex items-center gap-3 pl-1">
        <button
          type="button"
          onClick={() => {
            onChange('url');
          }}
          className={
            format === 'url'
              ? 'font-medium text-neutral-900 underline underline-offset-2'
              : 'hover:text-neutral-700'
          }
        >
          Windows (.url)
        </button>
        <button
          type="button"
          onClick={() => {
            onChange('webloc');
          }}
          className={
            format === 'webloc'
              ? 'font-medium text-neutral-900 underline underline-offset-2'
              : 'hover:text-neutral-700'
          }
        >
          macOS (.webloc)
        </button>
      </div>
    </details>
  );
}

function FolderPreview({
  pages,
  views,
  selectedViewKeys,
  onViewSelectedChange,
}: {
  readonly pages: readonly DesktopShortcutPage[];
  readonly views: readonly DesktopShortcutView[];
  readonly selectedViewKeys: ReadonlySet<string>;
  readonly onViewSelectedChange: (key: string, selected: boolean) => void;
}) {
  return (
    <div className="overflow-hidden rounded-md border border-neutral-200 bg-neutral-50">
      <div className="border-b border-neutral-200 bg-white px-3 py-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
          Folder preview
        </p>
      </div>
      <div className="max-h-[26rem] space-y-3 overflow-y-auto p-3">
        <section>
          <p className="mb-1.5 text-xs font-medium text-neutral-500">
            Pages — always included
          </p>
          <div className="space-y-1">
            <PreviewRow label="Library" />
            {pages.map((page) => (
              <PreviewRow key={page.id} label={page.name} />
            ))}
          </div>
        </section>
        <section>
          <p className="mb-1.5 text-xs font-medium text-neutral-500">
            Generated Views — choose what to include
          </p>
          {views.length === 0 ? (
            <p className="rounded bg-white px-2.5 py-2 text-xs text-neutral-500">
              No generated Views are available for your current Pages.
            </p>
          ) : (
            <div className="space-y-1 rounded bg-white p-1">
              {views.map((view) => (
                <div key={view.key} className="px-1.5 py-1">
                  <Checkbox
                    checked={selectedViewKeys.has(view.key)}
                    label={view.definitionName}
                    description={view.pageName}
                    onChange={(event) => {
                      onViewSelectedChange(view.key, event.target.checked);
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function PreviewRow({label}: {readonly label: string}) {
  return (
    <div className="rounded bg-white px-2.5 py-1.5 text-sm text-neutral-700">
      <span className="truncate">{label}</span>
    </div>
  );
}

/** Controlled preview and export surface for the user's desktop shortcuts. */
export function DesktopShortcutsDialog({
  open,
  isSupported,
  hasFolder,
  folderName,
  pages,
  views,
  selectedViewKeys,
  selectedViewCount,
  onViewSelectedChange,
  format,
  onFormatChange,
  isWorking,
  progress,
  onClose,
  onChooseFolder,
  onUpdate,
  onDownloadZip,
}: Props) {
  const shortcutCount = 1 + pages.length + selectedViewCount;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="max-h-[90vh] p-0" size="lg">
        <DialogHeader className="mb-0 flex flex-row items-center justify-between border-b border-neutral-100 px-6 py-4">
          <div>
            <DialogTitle className="text-lg font-semibold text-neutral-900">
              Desktop shortcuts
            </DialogTitle>
            <p className="mt-1 text-sm text-neutral-500">
              Choose the shortcuts that will be saved to your folder.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-700"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </DialogHeader>

        <div className="space-y-3 px-6 py-5">
          <FolderPreview
            pages={pages}
            views={views}
            selectedViewKeys={selectedViewKeys}
            onViewSelectedChange={onViewSelectedChange}
          />
          <p className="text-sm font-medium text-neutral-700">
            {shortcutCount} {shortcutCount === 1 ? 'shortcut' : 'shortcuts'}{' '}
            will be saved
          </p>
          {hasFolder && folderName ? (
            <p className="flex items-center gap-1.5 text-xs text-neutral-500">
              <FolderOpen className="h-3.5 w-3.5 shrink-0" />
              Saving to <span className="font-medium">{folderName}</span>
            </p>
          ) : null}
          {progress ? (
            <p className="text-xs text-neutral-500">
              Writing {progress.written} of {progress.total}…
            </p>
          ) : null}
          <FormatOptions format={format} onChange={onFormatChange} />
          <p className="text-xs text-neutral-400">
            Only already-generated Views appear here, so saving shortcuts never
            starts a new View generation.
          </p>
        </div>

        <DialogFooter className="mt-0 border-t border-neutral-100 px-6 py-4">
          {isSupported ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                disabled={isWorking}
                onClick={hasFolder ? onChooseFolder : onDownloadZip}
              >
                {hasFolder ? 'Choose another folder…' : 'Download zip'}
              </Button>
              <Button
                variant="dark"
                size="sm"
                loading={isWorking}
                onClick={hasFolder ? onUpdate : onChooseFolder}
              >
                {hasFolder ? 'Update folder' : 'Choose folder…'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={onClose}>
                Cancel
              </Button>
              <Button variant="dark" size="sm" onClick={onDownloadZip}>
                Download zip
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
